// =============================================================================
// SIREPPAR - Componente DataTable Reutilizable
// =============================================================================

import { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';

// =============================================================================
// Tipos
// =============================================================================

export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  width?: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyMessage?: string;
  
  // Paginación
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
  };
  
  // Ordenamiento
  sorting?: {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    onSort: (key: string) => void;
  };
  
  // Selección
  selection?: {
    selected: string[];
    onSelect: (ids: string[]) => void;
    idKey: keyof T;
  };
  
  // Acciones por fila
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T, index: number) => string;
}

// =============================================================================
// Componente
// =============================================================================

export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyMessage = 'No hay datos para mostrar',
  pagination,
  sorting,
  selection,
  onRowClick,
  rowClassName,
}: DataTableProps<T>) {
  const [localSort, setLocalSort] = useState<{ key: string; order: 'asc' | 'desc' } | null>(null);

  // Ordenamiento local si no hay sorting externo
  const sortedData = useMemo(() => {
    if (sorting || !localSort) return data;
    
    return [...data].sort((a, b) => {
      const aVal = a[localSort.key];
      const bVal = b[localSort.key];
      
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      const comparison = aVal < bVal ? -1 : 1;
      return localSort.order === 'asc' ? comparison : -comparison;
    });
  }, [data, localSort, sorting]);

  const handleSort = (key: string) => {
    if (sorting) {
      sorting.onSort(key);
    } else {
      setLocalSort(prev => {
        if (prev?.key === key) {
          return prev.order === 'asc' ? { key, order: 'desc' } : null;
        }
        return { key, order: 'asc' };
      });
    }
  };

  const isAllSelected = selection && data.length > 0 && 
    data.every(row => selection.selected.includes(String(row[selection.idKey])));

  const toggleSelectAll = () => {
    if (!selection) return;
    
    if (isAllSelected) {
      selection.onSelect([]);
    } else {
      selection.onSelect(data.map(row => String(row[selection.idKey])));
    }
  };

  const toggleSelectRow = (id: string) => {
    if (!selection) return;
    
    if (selection.selected.includes(id)) {
      selection.onSelect(selection.selected.filter(s => s !== id));
    } else {
      selection.onSelect([...selection.selected, id]);
    }
  };

  const getValue = (row: T, key: string): any => {
    return key.split('.').reduce((obj, k) => obj?.[k], row);
  };

  // Información de paginación
  const paginationInfo = pagination ? {
    from: (pagination.page - 1) * pagination.pageSize + 1,
    to: Math.min(pagination.page * pagination.pageSize, pagination.total),
    total: pagination.total,
    totalPages: Math.ceil(pagination.total / pagination.pageSize),
  } : null;

  return (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      {/* Tabla */}
      <div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* Checkbox de selección */}
              {selection && (
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
              )}
              
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  } ${column.className || ''}`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(String(column.key))}
                >
                  <div className="flex items-center gap-1">
                    {column.header}
                    {column.sortable && (
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              // Skeleton loading
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {selection && <td className="px-4 py-4"><div className="h-4 w-4 bg-gray-200 rounded animate-pulse" /></td>}
                  {columns.map((col, j) => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selection ? 1 : 0)} className="px-6 py-12 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => {
                const rowId = selection ? String(row[selection.idKey]) : '';
                const isSelected = selection?.selected.includes(rowId);
                
                return (
                  <tr
                    key={index}
                    className={`
                      ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
                      ${isSelected ? 'bg-primary-50' : ''}
                      ${rowClassName ? rowClassName(row, index) : ''}
                    `}
                    onClick={() => onRowClick?.(row)}
                  >
                    {selection && (
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectRow(rowId)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                    )}
                    
                    {columns.map((column) => (
                      <td
                        key={String(column.key)}
                        className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${column.className || ''}`}
                      >
                        {column.render
                          ? column.render(getValue(row, String(column.key)), row, index)
                          : getValue(row, String(column.key))
                        }
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {pagination && paginationInfo && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page >= paginationInfo.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
          
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{paginationInfo.from}</span> a{' '}
                <span className="font-medium">{paginationInfo.to}</span> de{' '}
                <span className="font-medium">{paginationInfo.total}</span> resultados
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Selector de página */}
              {pagination.onPageSizeChange && (
                <select
                  value={pagination.pageSize}
                  onChange={(e) => pagination.onPageSizeChange?.(Number(e.target.value))}
                  className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size} por página</option>
                  ))}
                </select>
              )}
              
              {/* Botones de navegación */}
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => pagination.onPageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                {/* Números de página */}
                {Array.from({ length: Math.min(5, paginationInfo.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (paginationInfo.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= paginationInfo.totalPages - 2) {
                    pageNum = paginationInfo.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => pagination.onPageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.page === pageNum
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => pagination.onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= paginationInfo.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
